import { Eye, EyeOff } from "lucide-react";
// eslint-disable-next-line no-unused-vars
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "motion/react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { GoogleLogin } from "@react-oauth/google";
import useAuthStore from "../stores/authStore";
import BrandLogo from "@/components/BrandLogo";
import api from "./services/api";
import { isValidGmail, isValidIndianMobile, normalizeEmail, normalizeIndianPhone } from "@/utils/userValidation";

import { Loader } from "@/components/ui/loader";
export default function Login() {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const role = "learner";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState("");
  // Submit lock to prevent double-submit. The auth flow has multiple
  // async legs (signup → auto-login → store update → App-level redirect)
  // and during that window the button must be inert. We do NOT reset
  // this flag on success — App.jsx routes away to /onboarding or
  // /<role> as soon as the auth store updates, which unmounts this page.
  // Only error paths reset the lock so the user can retry.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const login = useAuthStore((state) => state.login);

  // If the user actually sees this page, they aren't logged in. Wipe old flags to be safe.
  useEffect(() => {
    console.log("[VISIONRISE DEBUG - LOGIN PAGE] Login UI mounted. Clearing any old sessionStorage flags.");
    sessionStorage.removeItem("isNewSignUp");
  }, []);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { damping: 25, stiffness: 150 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);
  const rotateX = useTransform(smoothY, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(smoothX, [-0.5, 0.5], [-5, 5]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleAuth = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }
    // Hard lock against double-submit. If a request is already in
    // flight, ignore subsequent clicks/Enter-key fires entirely. This
    // is the FIRST thing we do — even before validation — because the
    // user might Enter-mash while the spinner is up.
    if (isSubmitting) {
      console.log("[VISIONRISE DEBUG - LOGIN PAGE] Submit ignored — already in flight.");
      return;
    }

    console.log(`[VISIONRISE DEBUG - LOGIN PAGE] User clicked ${isSignup ? "Sign Up" : "Sign In"}. Processing...`);
    setError("");

    // Validation runs without taking the lock — failed validation
    // shouldn't disable the button (the user wants to fix and retry).
    if (!/^\d{4}$/.test(pin)) {
      console.log("[VISIONRISE DEBUG - LOGIN PAGE] Failed: PIN is not 4 digits.");
      return setError("PIN must be exactly 4 digits");
    }

    if (isSignup) {
      if (!name.trim()) return setError("Name is required");
      if (!isValidGmail(email)) return setError("Please enter a valid email address");
      if (!isValidIndianMobile(phone)) return setError("Phone number must be a valid 10-digit Indian mobile number");
    } else if (!email) {
      return setError("Email is required");
    }

    // Take the lock NOW — everything below is a network leg.
    setIsSubmitting(true);

    try {
      if (isSignup) {
        console.log("[VISIONRISE DEBUG - LOGIN PAGE] Calling Backend /auth/signup...");
        await api.post("/auth/signup", {
          name,
          email: normalizeEmail(email),
          phone: normalizeIndianPhone(phone),
          pin,
          role,
        });
        
        console.log("[VISIONRISE DEBUG - LOGIN PAGE] Signup successful! Setting isNewSignUp = true in sessionStorage.");
        sessionStorage.setItem("isNewSignUp", "true");
        toast.success("Account created! Check your email for a verification code.");
        
        console.log("[VISIONRISE DEBUG - LOGIN PAGE] Calling Backend /auth/login for auto-login...");
        const { data } = await api.post("/auth/login", { email, pin });
        const payload = data?.data || data;
        
        console.log("[VISIONRISE DEBUG - LOGIN PAGE] Auto-login successful. Updating Zustand store.");
        login(payload.user, payload.accessToken || payload.token, payload.isGlobalOnboardingRequired);
        // -> App.jsx takes over here and routes to /onboarding
        
      } else {
        console.log("[VISIONRISE DEBUG - LOGIN PAGE] Calling Backend /auth/login for existing user...");
        sessionStorage.removeItem("isNewSignUp"); // Strictly ensure flag is false

        const { data } = await api.post("/auth/login", { email, pin });
        const payload = data?.data || data;
        console.log("[VISIONRISE DEBUG - LOGIN PAGE] Sign In successful! Updating Zustand store.");
        login(payload.user, payload.accessToken || payload.token, payload.isGlobalOnboardingRequired);
        // -> App.jsx takes over here and routes to /learner
      }
    } catch (err) {
      console.error("[VISIONRISE DEBUG - LOGIN PAGE] API Error:", err);
      const notVerifiedCode = err?.response?.data?.code;
      const tempToken = err?.response?.data?.tempToken;

      if (notVerifiedCode === "EMAIL_NOT_VERIFIED" && tempToken) {
        toast.error("Please verify your email before logging in.");
        // Keep the lock — we're navigating away anyway.
        navigate("/verify-email", {
          state: {
            tempToken,
            email: normalizeEmail(email),
          },
        });
        return;
      }

      setError(err.response?.data?.msg || err.response?.data?.message || "Server not responding. Please try again later.");
      // Real failure — release the lock so the user can fix and retry.
      setIsSubmitting(false);
    }
    // NOTE: no `finally` reset here. On the success path the auth store
    // updates and App.jsx routes away within the same render cycle, so
    // this component unmounts before any further interaction is possible.
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      setError("");
      const { data } = await api.post("/auth/google-login", {
        credential: credentialResponse.credential,
      });
      const payload = data?.data || data;

      login(payload.user, payload.accessToken || payload.token, payload.isGlobalOnboardingRequired);
      // App.jsx takes over here and handles routing based on role.
      // Don't release the lock — component is about to unmount.
    } catch (err) {
      console.error("Google Auth Error:", err);
      setError(err.response?.data?.msg || "Google authentication failed. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleEnterFlow = (event) => {
    if (event.key !== "Enter") return;

    const flowRoot = event.currentTarget;
    const currentNode = event.target.closest("[data-enter-order]");
    if (!flowRoot || !currentNode) return;

    const orderedNodes = Array.from(flowRoot.querySelectorAll("[data-enter-order]"))
      .sort((a, b) => Number(a.dataset.enterOrder) - Number(b.dataset.enterOrder));

    const currentIndex = orderedNodes.findIndex((node) => node === currentNode);
    if (currentIndex === -1) return;

    if (currentIndex === orderedNodes.length - 1) {
      event.preventDefault();
      handleAuth();
      return;
    }

    event.preventDefault();
    const nextNode = orderedNodes[currentIndex + 1];
    const nextFocusable = nextNode.matches("input,textarea,button,select")
      ? nextNode
      : nextNode.querySelector("input,textarea,button,select");

    nextFocusable?.focus();
  };

  return (
    <div
      className="flex min-h-screen w-full flex-col items-center justify-center bg-background text-foreground p-4"
      onMouseMove={handleMouseMove}
    >
      <div className="mb-8 flex justify-center">
        <BrandLogo alt="Institute logo" className="h-32 w-auto object-contain sm:h-44" />
      </div>

      <div className="relative z-10 w-full max-w-[32rem] sm:max-w-[38rem]">
        <Card className="w-full border-border bg-card shadow-2xl ring-1 ring-border sm:bg-card">
          <CardHeader className="space-y-1">
            <motion.div
              className="relative cursor-pointer rounded-xl px-4 py-2 transition-all"
              onClick={() => {
                const el = document.getElementById("title-bg");
                if (el) { el.style.opacity = "1"; setTimeout(() => { el.style.opacity = "0"; }, 600); }
              }}
              whileTap={{ scale: 0.98 }}
            >
              <div
                className="pointer-events-none absolute inset-0 rounded-full bg-primary/10 opacity-0 blur-xl transition-opacity duration-500"
                id="title-bg"
              />
              <AnimatePresence mode="wait">
                <motion.div animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} initial={{ opacity: 0, y: -10 }} key={isSignup ? "signup-title" : "login-title"} transition={{ duration: 0.2 }}>
                  <CardTitle className="relative z-10 text-center font-bold text-2xl">
                    {isSignup ? "Create an account" : "Welcome"}
                  </CardTitle>
                </motion.div>
              </AnimatePresence>
            </motion.div>
            <AnimatePresence mode="wait">
              <motion.div animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} initial={{ opacity: 0, x: -10 }} key={isSignup ? "signup-desc" : "login-desc"} transition={{ duration: 0.2 }}>
                <CardDescription className="text-center">
                  {isSignup ? "Enter your details below to create your account" : "Enter your email and PIN to login"}
                </CardDescription>
              </motion.div>
            </AnimatePresence>
          </CardHeader>
          <CardContent>
            <form id="auth-form" autoComplete="on" onSubmit={handleAuth}>
              <Tabs
                className="w-full"
                onValueChange={(v) => {
                  setIsSignup(v === "signup");
                  setName("");
                  setEmail("");
                  setPhone("");
                  setPin("");
                  setShowPin(false);
                  setError("");
                }}
                value={isSignup ? "signup" : "login"}
              >
              <TabsList className="relative mb-4 grid w-full grid-cols-2 bg-muted p-1">
                <TabsTrigger
                  className="relative z-10 transition-none data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  value="login"
                >
                  {isSignup === false && (
                    <motion.div
                      className="absolute inset-0 z-[-1] rounded-md bg-background shadow-sm"
                      initial={false}
                      layoutId="active-auth-tab"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10 text-muted-foreground font-medium data-[state=active]:text-foreground">Sign In</span>
                </TabsTrigger>
                <TabsTrigger
                  className="relative z-10 transition-none data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  value="signup"
                >
                  {isSignup === true && (
                    <motion.div
                      className="absolute inset-0 z-[-1] rounded-md bg-background shadow-sm"
                      initial={false}
                      layoutId="active-auth-tab"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <span className="relative z-10 text-muted-foreground font-medium data-[state=active]:text-foreground">Sign Up</span>
                </TabsTrigger>
              </TabsList>

              <AnimatePresence mode="wait">
                <motion.div animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: isSignup ? -20 : 20 }} initial={{ opacity: 0, x: isSignup ? 20 : -20 }} key={isSignup ? "signup-content" : "login-content"} transition={{ duration: 0.3, ease: "easeInOut" }}>
                  <TabsContent className="space-y-4" value="login" onKeyDown={handleEnterFlow}>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        data-enter-order="1"
                        className={cn(
                          "transition-all duration-300",
                          email &&
                          "border-primary bg-primary/5 shadow-sm"
                        )}
                        id="email"
                        name="email"
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="user@example.com"
                        type="email"
                        autoComplete="username email"
                        value={email}
                      />
                    </div>
                    {/* Keep a real password field in the form for browser save/autofill detection. */}
                    <input
                      aria-hidden="true"
                      className="sr-only"
                      name="password"
                      tabIndex={-1}
                      type="password"
                      autoComplete="current-password"
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="pin">PIN</Label>
                        <button
                          className="flex items-center gap-1 text-muted-foreground text-xs transition-colors hover:text-foreground"
                          onClick={() => setShowPin(!showPin)}
                          type="button"
                        >
                          {showPin ? (
                            <EyeOff className="h-3.5 w-3.5" />
                          ) : (
                            <Eye className="h-3.5 w-3.5" />
                          )}
                          {showPin ? "Hide" : "Show"}
                        </button>
                      </div>
                      <div className="flex justify-center" data-enter-order="2">
                        {/* No name/autoComplete here: the sr-only <input
                            type=password> above is the sole autofill/save target.
                            Naming this one "password" too made the browser attach
                            its password overlay + masking to the OTP's internal
                            input, breaking the caret (BUG-06) and the Show toggle
                            (BUG-08). */}
                        <InputOTP
                          inputMode="numeric"
                          maxLength={4}
                          onChange={setPin}
                          value={pin}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} mask={!showPin} />
                            <InputOTPSlot index={1} mask={!showPin} />
                            <InputOTPSlot index={2} mask={!showPin} />
                            <InputOTPSlot index={3} mask={!showPin} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent className="space-y-4" value="signup" onKeyDown={handleEnterFlow}>
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        data-enter-order="1"
                        className={cn(
                          "transition-all duration-300",
                          name &&
                          "border-primary bg-primary/5 shadow-sm"
                        )}
                        id="name"
                        onChange={(e) => setName(e.target.value.replace(/[^A-Za-z\s]/g, ""))}
                        placeholder="John Doe"
                        value={name}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email-signup">Email</Label>
                      <Input
                        data-enter-order="2"
                        className={cn(
                          "transition-all duration-300",
                          email &&
                          "border-primary bg-primary/5 shadow-sm"
                        )}
                        id="email-signup"
                        name="email"
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="user@example.com"
                        type="email"
                        autoComplete="email"
                        value={email}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        data-enter-order="3"
                        className={cn(
                          "transition-all duration-300",
                          phone &&
                          "border-primary bg-primary/5 shadow-sm"
                        )}
                        id="phone"
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        maxLength={10}
                        placeholder="9876543210"
                        value={phone}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="pin-signup">Set 4-digit PIN</Label>
                        <button
                          className="flex items-center gap-1 text-muted-foreground text-xs transition-colors hover:text-foreground"
                          onClick={() => setShowPin(!showPin)}
                          type="button"
                        >
                          {showPin ? (
                            <EyeOff className="h-3.5 w-3.5" />
                          ) : (
                            <Eye className="h-3.5 w-3.5" />
                          )}
                          {showPin ? "Hide" : "Show"}
                        </button>
                      </div>
                      <input
                        aria-hidden="true"
                        className="sr-only"
                        name="new-password"
                        tabIndex={-1}
                        type="password"
                        autoComplete="new-password"
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      />
                      <div className="flex justify-center" data-enter-order="4">
                        <InputOTP
                          inputMode="numeric"
                          maxLength={4}
                          onChange={setPin}
                          value={pin}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} mask={!showPin} />
                            <InputOTPSlot index={1} mask={!showPin} />
                            <InputOTPSlot index={2} mask={!showPin} />
                            <InputOTPSlot index={3} mask={!showPin} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                  </TabsContent>
                </motion.div>
              </AnimatePresence>
              </Tabs>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <div className="mt-6 flex justify-center w-full">
                <div className="w-full flex justify-center [&>div]:w-full [&>div>div]:w-full [&_iframe]:w-full">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError("Google Sign-In was unsuccessful. Try again later.")}
                    size="large"
                    theme="outline"
                    shape="rectangular"
                    width="100%"
                    text={isSignup ? "signup_with" : "signin_with"}
                  />
                </div>
              </div>
            </div>

          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <AnimatePresence>
              {error && (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="flex w-full items-start gap-2.5 rounded-lg border border-destructive/20 bg-destructive-subtle px-3 py-2.5"
                  exit={{ opacity: 0, y: -4 }}
                  initial={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Accent bar */}
                  <span className="mt-0.5 h-full w-1 shrink-0 self-stretch rounded-full bg-destructive" />
                  <p className="text-center font-medium text-destructive-foreground text-sm leading-snug">
                    {error}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-70 disabled:cursor-not-allowed"
              type="submit"
              form="auth-form"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader size="xs" className="mr-2" />
                  {isSignup ? "Creating account..." : "Signing in..."}
                </>
              ) : (
                isSignup ? "Create Account" : "Sign In"
              )}
            </Button>

            <button
              className="text-primary font-medium text-sm transition-colors hover:underline"
              onClick={() => { }}
              type="button"
            >
              <Link to="/forgot-pin">Forgot PIN?</Link>
            </button>

            <p className="text-center text-muted-foreground text-sm">
              {isSignup ? "Already have an account?" : "Don't have an account?"}{" "}
              <button
                type="button"
                className="text-primary font-medium transition-colors hover:underline"
                onClick={() => {
                  setIsSignup(!isSignup);
                  setName("");
                  setEmail("");
                  setPhone("");
                  setPin("");
                  setShowPin(false);
                  setError("");
                }}
              >
                {isSignup ? "Sign in" : "Sign up"}
              </button>
            </p>

            <p className="px-8 text-center text-muted-foreground text-xs">
              By clicking continue, you agree to our{" "}
              <a className="underline underline-offset-4 hover:text-foreground" href="#">
                Terms of Service
              </a>{" "}
              and{" "}
              <a className="underline underline-offset-4 hover:text-foreground" href="#">
                Privacy Policy
              </a>
              .
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
